import {
  useRef,
  useState,
  useEffect,
  memo,
} from 'react';
import NotFoundImage from 'assets/images/404';
import ColorThief from 'colorthief';
import { ParagraphMedium } from 'library/styles/text';
import useThrottle from 'library/hooks/use-throttle';
import useDebounce from 'library/hooks/use-debounce';
import {
  Container,
  CanvasContainer,
  StyledCursor,
  Preview,
  ButtonFile,
  PreviewBackground,
  Footer,
  Button,
  ButtonPick,
  Icon,
  TriangleContainer,
  StyledCanvas,
} from './styles';

const colorToHex = (color) => {
  const hexadecimal = color.toString(16);

  return hexadecimal.length === 1 ? `0${hexadecimal}` : hexadecimal;
};

export const rgbToHex = (red, green, blue) => `#${colorToHex(red)}${colorToHex(green)}${colorToHex(blue)}`;

const MAX_WIDTH = 620;

function getMousePos(canvas, { clientX, clientY }) {
  const rect = canvas.getBoundingClientRect();

  return {
    // eslint-disable-next-line no-mixed-operators
    x: (clientX - rect.left) / (rect.right - rect.left) * canvas.width,
    // eslint-disable-next-line no-mixed-operators
    y: (clientY - rect.top) / (rect.bottom - rect.top) * canvas.height,
  };
}

const Palette = ({
  onChange, value, image, onImageReset,
}) => {
  const [palette, setPalette] = useState([]);
  const [isCursorDisplayed, setIsCursorDisplayed] = useState(false);
  const [isCursorStopped, setCursorStopped] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(null);
  const canvas = useRef(null);
  const ctx = useRef(null);
  // const isImageLoaded = image?.element?.complete;
  const [coordinates, setCoordinates] = useState({
    clientX: undefined,
    clientY: undefined,
  });

  const debouncedPrimaryColor = useDebounce(primaryColor, 100);

  const resetToDefaults = () => {
    setIsCursorDisplayed(false);
    setCursorStopped(false);
    setPrimaryColor(null);
    setCoordinates({
      clientX: undefined,
      clientY: undefined,
    });
  };

  const getColorAt = (x, y) => {
    if (ctx.current && x && y) {
      const [R, G, B] = ctx.current.getImageData(x, y, 1, 1).data;

      return rgbToHex(R, G, B);
    }

    return null;
  };

  useEffect(() => {
    if (palette.length && !isCursorDisplayed) {
      setIsCursorDisplayed(true);
    }
  }, [palette.length]);

  useEffect(() => {
    if (image) {
      // const imageCopy = new Image();
      const colorThief = new ColorThief();

      image?.element.addEventListener('load', () => {
        ctx.current = canvas.current.getContext('2d');
        ctx.current.imageSmoothingEnabled = true;

        const iw = image.width || image.element.naturalWidth;
        const ih = image.height || image.element.naturalHeight;
        const scale = Math.min(MAX_WIDTH / iw);
        const iwScaled = iw * scale;
        const ihScaled = ih * scale;

        canvas.current.width = iwScaled;
        canvas.current.height = ihScaled;

        ctx.current.drawImage(image.element, 0, 0, iwScaled, ihScaled);

        const pallette = colorThief.getPalette(image.element, 5);
        const dominantColor = colorThief.getColor(image.element);

        const paletteValues = [
          ...pallette.map(([R, G, B]) => rgbToHex(R, G, B)),
          rgbToHex(...dominantColor),
        ];

        setPalette(paletteValues);
      }, false);
    } else {
      setPalette([]);
      onImageReset();
    }
  }, [image]);

  useEffect(() => {
    setPrimaryColor(getColorAt(coordinates.clientX, coordinates.clientY));
  }, [coordinates.clientX, coordinates.clientY]);

  const handleChange = (e) => {
    const [file] = e.target.files;

    if (file) {
      resetToDefaults();
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();

      ctx.current = canvas.current.getContext('2d');
      ctx.current.imageSmoothingEnabled = true;

      img.onload = () => {
        const iw = img.width || img.naturalWidth;
        const ih = img.height || img.naturalHeight;
        const scale = Math.min(MAX_WIDTH / iw);
        const iwScaled = iw * scale;
        const ihScaled = ih * scale;

        canvas.current.width = iwScaled;
        canvas.current.height = ihScaled;

        ctx.current.drawImage(img, 0, 0, iwScaled, ihScaled);

        const colorThief = new ColorThief();
        const pallette = colorThief.getPalette(img, 5);
        const dominantColor = colorThief.getColor(img);
        const paletteValues = [
          ...pallette.map(([R, G, B]) => rgbToHex(R, G, B)),
          rgbToHex(...dominantColor),
        ];

        setPalette(paletteValues);
      };

      img.src = event.target.result;
    };

    reader.readAsDataURL(file);
  };

  const handlePickerCursorHide = () => {
    if (!isCursorStopped) {
      setIsCursorDisplayed(false);

      setCoordinates({
        clientX: undefined,
        clientY: undefined,
      });
    }
  };

  const handlePickerCursorMove = useThrottle((e) => {
    if (!isCursorStopped) {
      const { x, y } = getMousePos(canvas.current, {
        clientX: e.clientX,
        clientY: e.clientY,
      });

      requestAnimationFrame(() => {
        setCoordinates({
          clientX: x,
          clientY: y,
        });
      });

      if (!isCursorDisplayed) {
        setIsCursorDisplayed(true);
      }
    }
  }, 50);

  const handleImageClick = (e) => {
    const { x, y } = getMousePos(canvas.current, {
      clientX: e.clientX,
      clientY: e.clientY,
    });

    setCoordinates({
      clientX: x,
      clientY: y,
    });

    setCursorStopped((prevState) => !prevState);
  };

  const { clientX, clientY } = coordinates;

  const handleAddColor = () => {
    onChange([primaryColor]);
  };

  const handleAddPallette = () => {
    onChange(palette);
  };

  return (
    <Container>
      <ButtonFile>
        <Icon className="icon-picture" />
        Add image from ...
        <input type="file" id="uploadImage" name="uploadImage" onChange={handleChange} />
      </ButtonFile>
      <Preview>
        {!palette.length && (
          <PreviewBackground>
            <NotFoundImage />
            <ParagraphMedium>You can upload your own photos to pick color from</ParagraphMedium>
          </PreviewBackground>
        )}
        <CanvasContainer isEmpty={palette.length === 0}>
          {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
          <StyledCanvas
            ref={canvas}
            clientX={clientX}
            clientY={clientY}
            onClick={handleImageClick}
            onMouseMove={handlePickerCursorMove}
            onMouseOut={handlePickerCursorHide}
            isPaused={isCursorStopped}
          />
          {
            (isCursorDisplayed && clientX && clientY)
              ? (
                <StyledCursor
                  clientX={clientX}
                  clientY={clientY}
                  isPaused={isCursorStopped}
                  color={debouncedPrimaryColor}
                >
                  <TriangleContainer />
                </StyledCursor>
              )
              : null
          }
        </CanvasContainer>
      </Preview>
      {palette.length ? (
        <Footer>
          <Button
            onClick={handleAddColor}
            type="button"
            disabled={!isCursorStopped || value.includes(primaryColor)}
          >
            <span>
              Add color
            </span>
          </Button>
          <ButtonPick
            onClick={handleAddPallette}
            disabled={isCursorStopped}
            type="button"
          >
            Auto pick
          </ButtonPick>
        </Footer>
      ) : null}
    </Container>
  );
};

export default memo(Palette);
